import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { Box } from '@mui/material';
import { ControllerRenderProps } from 'react-hook-form';
import {
  ProjectStackField,
  ProjectStackDocument,
  unsanitizeProjectFieldName,
} from '~/src/entities/project';
import {
  EntityType,
  FieldType,
  StringFieldType,
} from '~/src/entities/project/types';
import { ContactSource } from '~/src/entities/contact';
import {
  useAIFieldMapperEnabled,
  useAnnotationsDateFieldPickerEnabled,
} from '~/src/entities/user';
import {
  useDeleteFieldMapping,
  useDeleteMappingsBySourceAttribute,
  useMarkCoreFieldModified,
  useIsValueApplied,
  FieldMappingResponse,
} from '~/src/entities/fieldMapping';
import { getFieldMappingOverrideSubjectType } from '~/src/entities/fieldMapping/services/fieldMappingService';
import { ShortTextField } from './ShortTextField';
import { LongTextField } from './LongTextField';
import { SingleSelectField } from './SingleSelectField';
import { MultipleChoiceField } from './MultipleChoiceField';
import { DatePickerField } from './DatePickerField';
import {
  FieldMappingBadge,
  CoreFieldBadge,
} from '~/src/components/FieldMappingBadge';
import { useProjectContext } from '~/src/pages/Populate/ProjectProvider';

export type FieldRenderProps = ControllerRenderProps & {
  onBlur: () => void;
  onFocus: () => void;
  countTouched?: (fieldId: string) => void;
};

export type PopulateCardFieldProps = {
  field: FieldRenderProps;
  projectField: ProjectStackField;
  projectStackDocuments: ProjectStackDocument[];
  isFieldMappingOpen: boolean;
  onFieldMappingOpen: () => void;
  onFieldMappingClose: () => void;
  isRoleCard: boolean;
  contactId?: number;
  contactSource?: ContactSource;
};

const componentForField = (field: ProjectStackField) => {
  const hasOptions = !!field.multiple_choice_variable?.options;

  if (field.picklist_options && field.picklist_options.length > 0)
    return SingleSelectField;

  if (
    [FieldType.LONG_TEXT, StringFieldType.LONG_TEXT].includes(
      field.variable_type,
    )
  )
    return LongTextField;

  if ([FieldType.DATE, StringFieldType.DATE].includes(field.variable_type))
    return DatePickerField;

  if (
    [FieldType.BATCH_SELECT, StringFieldType.BATCH_SELECT].includes(
      field.variable_type,
    ) &&
    hasOptions
  )
    return MultipleChoiceField;

  if (
    [FieldType.MULTIPLE_CHOICE, StringFieldType.MULTIPLE_CHOICE].includes(
      field.variable_type,
    ) &&
    hasOptions
  )
    return SingleSelectField;

  return ShortTextField;
};

export const PopulateCardField = ({
  field,
  projectField,
  projectStackDocuments,
  isFieldMappingOpen,
  onFieldMappingOpen,
  onFieldMappingClose,
  isRoleCard,
  contactId,
  contactSource,
}: PopulateCardFieldProps) => {
  const project = useProjectContext();
  const dateFormat = project.stack_data.project_metadata?.date_format;
  const FieldComponent = componentForField(projectField);
  const isAIfieldMapperEnabled = useAIFieldMapperEnabled();
  const isDateFieldPickerEnabled = useAnnotationsDateFieldPickerEnabled();
  const [isMappingDeletedLocally, setIsMappingDeletedLocally] = useState(false);
  const [isCoreFieldModifiedLocally, setIsCoreFieldModifiedLocally] =
    useState(false);

  // Reset local modified state when contact changes (different contact = fresh state)
  useEffect(() => {
    setIsCoreFieldModifiedLocally(false);
  }, [contactId]);

  const deleteMappingMutation = useDeleteFieldMapping();
  const deleteMappingsBySourceAttributeMutation =
    useDeleteMappingsBySourceAttribute();
  const markCoreFieldModifiedMutation = useMarkCoreFieldModified();

  const metadata = projectField.field_mapping_metadata;

  const isSourceField = !!projectField.core_field_source;

  const shouldShowBadge = useMemo(() => {
    if (!isAIfieldMapperEnabled || isSourceField) {
      return false;
    }

    return !isRoleCard || (!!contactId && contactSource === 'CLIO');
  }, [
    isAIfieldMapperEnabled,
    isRoleCard,
    isSourceField,
    contactId,
    contactSource,
  ]);

  const isMappingFeatureActive =
    isAIfieldMapperEnabled &&
    (!isRoleCard || (!!contactId && contactSource === 'CLIO'));

  const isValueApplied = useIsValueApplied({
    metadata,
    fieldId: field.name,
    variableType: projectField.variable_type,
    isRoleCard,
    contactId,
  });

  const hasActiveTargetMapping =
    isValueApplied &&
    isMappingFeatureActive &&
    !isMappingDeletedLocally &&
    !!metadata?.source_field;

  const hasCoreFieldActive =
    isMappingFeatureActive &&
    !!projectField.core_field_source &&
    projectField.core_field_source.entity_type !== EntityType.MATTER &&
    !projectField.core_field_source.is_modified &&
    !isCoreFieldModifiedLocally;

  const sourceAttributeName = (() => {
    if (!isSourceField) return null;
    const fieldName = unsanitizeProjectFieldName(projectField.fid);
    return fieldName.split('.').pop() || fieldName;
  })();

  // SOURCE fields: check if any field in the project depends on this field as a source
  const hasActiveDependentMappings = useMemo(() => {
    if (!isMappingFeatureActive || !sourceAttributeName) return false;

    return project.stack_data.fields.some((field) => {
      const mappedSourceField = field.field_mapping_metadata?.source_field;
      if (!mappedSourceField) return false;
      return (
        mappedSourceField.endsWith(`.${sourceAttributeName}`) ||
        mappedSourceField === sourceAttributeName
      );
    });
  }, [isMappingFeatureActive, sourceAttributeName, project.stack_data.fields]);

  const handleTargetMappingDeletion = useCallback(async () => {
    const mappingId = metadata?.field_mapping_id;
    if (mappingId) {
      await deleteMappingMutation.mutateAsync({
        projectId: project.id,
        mappingId,
      });
      setIsMappingDeletedLocally(true);
    }
  }, [metadata?.field_mapping_id, project.id, deleteMappingMutation]);

  const handleSourceMappingsDeletion = useCallback(async () => {
    if (!sourceAttributeName) return;
    await deleteMappingsBySourceAttributeMutation.mutateAsync({
      projectId: project.id,
      sourceField: sourceAttributeName,
    });
  }, [
    sourceAttributeName,
    project.id,
    deleteMappingsBySourceAttributeMutation,
  ]);

  const handleMappingChanged = useCallback(
    (response?: FieldMappingResponse) => {
      setIsMappingDeletedLocally(!response);
    },
    [],
  );

  // Handler for marking core field as modified when user starts typing
  // Only contacts support core field modification tracking.
  // Matter fields use stack_saved_data which persists through resync.
  const handleCoreFieldModified = useCallback(() => {
    const coreFieldSource = projectField.core_field_source;
    if (
      !coreFieldSource ||
      coreFieldSource.is_modified ||
      isCoreFieldModifiedLocally ||
      coreFieldSource.entity_type !== EntityType.CONTACT ||
      !contactId
    )
      return;

    // Optimistically update UI immediately
    setIsCoreFieldModifiedLocally(true);

    markCoreFieldModifiedMutation.mutate({
      entityType: EntityType.CONTACT,
      entityId: contactId,
      fieldName: coreFieldSource.source_field,
    });
  }, [
    projectField.core_field_source,
    contactId,
    markCoreFieldModifiedMutation,
    isCoreFieldModifiedLocally,
  ]);

  const hasMappingBeenDeleted = useRef(false);
  const hasActiveMapping = hasActiveTargetMapping || hasActiveDependentMappings;
  const prevHasActiveMapping = useRef(hasActiveMapping);

  // Store current values in refs for stable callback
  const mappingStateRef = useRef({
    hasActiveDependentMappings,
    hasActiveTargetMapping,
    hasCoreFieldActive,
    handleSourceMappingsDeletion,
    handleTargetMappingDeletion,
    handleCoreFieldModified,
  });

  // Update ref on every render so callback always has latest values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    mappingStateRef.current = {
      hasActiveDependentMappings,
      hasActiveTargetMapping,
      hasCoreFieldActive,
      handleSourceMappingsDeletion,
      handleTargetMappingDeletion,
      handleCoreFieldModified,
    };
  });

  const fieldMappingSubjectType =
    getFieldMappingOverrideSubjectType(isRoleCard);
  const fieldMappingSubjectId =
    fieldMappingSubjectType === 'matter'
      ? project.stack_data.project_metadata?.matter_id
      : contactId;
  const fieldMappingOverrideSubject =
    fieldMappingSubjectId != null
      ? { fieldMappingSubjectType, fieldMappingSubjectId }
      : undefined;

  useEffect(() => {
    if (hasActiveMapping && !prevHasActiveMapping.current) {
      hasMappingBeenDeleted.current = false;
    }
    prevHasActiveMapping.current = hasActiveMapping;
  }, [hasActiveMapping]);

  // Stable callback that reads from ref - identity never changes
  const onFirstChange = useCallback(() => {
    const {
      hasActiveDependentMappings: hasDependentMappings,
      hasActiveTargetMapping: hasTargetMapping,
      hasCoreFieldActive: hasCoreField,
      handleSourceMappingsDeletion: deleteSourceMappings,
      handleTargetMappingDeletion: deleteTargetMapping,
      handleCoreFieldModified: markCoreFieldModified,
    } = mappingStateRef.current;
    if (hasMappingBeenDeleted.current) return;

    if (hasDependentMappings) {
      hasMappingBeenDeleted.current = true;
      deleteSourceMappings();
    }

    if (hasTargetMapping) {
      hasMappingBeenDeleted.current = true;
      deleteTargetMapping();
    }

    // Mark core field as modified (optimistic UI + API call)
    if (hasCoreField) {
      hasMappingBeenDeleted.current = true;
      markCoreFieldModified();
    }
  }, []);

  return (
    <Box pt={1} sx={{ position: 'relative' }}>
      {/* Select Select and Multiple Choice fields do not break mapping when value is changed,
        this is a product requirement based on the nature of these two field types.
        Thus, onFirstChange is not passed to these two field components.
      */}
      {FieldComponent === SingleSelectField ? (
        <SingleSelectField
          field={field}
          projectField={projectField}
          projectStackDocuments={projectStackDocuments}
          hasActiveMapping={hasActiveTargetMapping}
          fieldMappingOverrideSubject={fieldMappingOverrideSubject}
        />
      ) : FieldComponent === MultipleChoiceField ? (
        <MultipleChoiceField
          field={field}
          projectField={projectField}
          projectStackDocuments={projectStackDocuments}
          hasActiveMapping={hasActiveTargetMapping}
          fieldMappingOverrideSubject={fieldMappingOverrideSubject}
        />
      ) : FieldComponent === DatePickerField ? (
        <DatePickerField
          field={field}
          projectField={projectField}
          projectStackDocuments={projectStackDocuments}
          onFirstChange={onFirstChange}
          hasActiveMapping={hasActiveTargetMapping || hasCoreFieldActive}
          orgDateFormat={dateFormat ?? undefined}
          isDateFieldPickerEnabled={isDateFieldPickerEnabled}
        />
      ) : FieldComponent === ShortTextField ? (
        <ShortTextField
          field={field}
          projectField={projectField}
          projectStackDocuments={projectStackDocuments}
          onFirstChange={onFirstChange}
          hasActiveMapping={hasActiveTargetMapping || hasCoreFieldActive}
          dateFormat={dateFormat}
        />
      ) : (
        <FieldComponent
          field={field}
          projectField={projectField}
          projectStackDocuments={projectStackDocuments}
          onFirstChange={onFirstChange}
          hasActiveMapping={hasActiveTargetMapping || hasCoreFieldActive}
        />
      )}
      {isAIfieldMapperEnabled &&
        projectField.core_field_source &&
        (!isRoleCard || (!!contactId && contactSource === 'CLIO')) &&
        projectField.core_field_source.entity_type !== EntityType.MATTER && (
          <CoreFieldBadge
            source={projectField.core_field_source}
            isModifiedLocally={isCoreFieldModifiedLocally}
          />
        )}
      {shouldShowBadge &&
        projectField.core_field_source?.entity_type !== EntityType.MATTER && (
          <FieldMappingBadge
            metadata={metadata}
            hasDeletedFieldMapping={projectField.has_deleted_field_mapping}
            onOpen={onFieldMappingOpen}
            onClose={onFieldMappingClose}
            onMappingCreated={handleMappingChanged}
            isOpen={isFieldMappingOpen}
            projectField={projectField}
            isRoleCard={isRoleCard}
            contactId={contactId}
            isMappingDeleted={isMappingDeletedLocally}
          />
        )}
    </Box>
  );
};
